import React, { useEffect, useState, useCallback } from "react";
import { GOVERNED_RECOMMENDATIONS } from "../data/governed_action";
import { getKatlasStatus, submitGovernedAction } from "../lib/katlas_client";
import { KatlasStatusBadge } from "./KatlasStatusBadge";
import {
  CheckCircle,
  XCircle,
  ShieldCheck,
  Lock,
  EyeSlash,
  Eye,
  FileText,
  ArrowRight,
  Warning,
} from "@phosphor-icons/react";

export const GovernedAction = () => {
  const [selectedId, setSelectedId] = useState(GOVERNED_RECOMMENDATIONS[0].id);
  const [katlas, setKatlas] = useState(null);
  const [working, setWorking] = useState(false);
  const [result, setResult] = useState(null); // { ok, receipt | error }

  const rec = GOVERNED_RECOMMENDATIONS.find((r) => r.id === selectedId);

  const refreshStatus = useCallback(async () => {
    try {
      const s = await getKatlasStatus();
      setKatlas(s);
    } catch {
      setKatlas({ status: "unreachable", label: "Backend unreachable", node_reachable: false });
    }
  }, []);

  useEffect(() => {
    refreshStatus();
    const id = window.setInterval(refreshStatus, 20000);
    return () => window.clearInterval(id);
  }, [refreshStatus]);

  // Clear last result whenever the user changes recommendation
  useEffect(() => setResult(null), [selectedId]);

  const decide = async (outcome) => {
    if (working) return;
    setWorking(true);
    setResult(null);
    const payload = {
      action: rec.action_class,
      outcome,
      actor: rec.actor,
      authority_gate: rec.authority_gate,
      custody: {
        evidence_refs: rec.custody.shared_evidence.map((e) => e.ref),
        withheld_count: rec.custody.withheld_evidence.length,
      },
      context: {
        recommendation_id: rec.id,
        summary: rec.title,
      },
    };
    const res = await submitGovernedAction(payload);
    setResult(res);
    setWorking(false);
  };

  return (
    <div className="flex-1 overflow-y-auto p-6 bg-[#F9F9F8] scroll-soft" data-testid="governed-action">
      <div className="max-w-[1200px] mx-auto grid grid-cols-12 gap-6">
        {/* Header card */}
        <section className="col-span-12 bg-white border border-stone-200 rounded-sm p-5 flex items-start justify-between gap-6">
          <div>
            <div className="text-[11px] uppercase tracking-[0.18em] text-stone-500">
              Primary governed action
            </div>
            <h2 className="text-2xl font-medium text-stone-900 mt-1 leading-tight">
              Approve or refuse an AI-assisted operational recommendation
            </h2>
            <p className="text-sm text-stone-700 mt-2 leading-relaxed max-w-2xl">
              AICierge turns AI-assisted recommendations, routing decisions and operational
              actions into governed workflows with KATLAS-signed receipts — showing who had
              authority, what evidence was used, what was shared, what was withheld, and what
              outcome was recorded.
            </p>
            <div className="mt-3 text-sm font-medium text-stone-900">
              AI suggests. Authorised roles decide. KATLAS signs the receipt.
            </div>
          </div>
          <div className="shrink-0">
            <KatlasStatusBadge status={katlas} />
          </div>
        </section>

        {/* Recommendation picker */}
        <section className="col-span-12">
          <div className="text-[11px] uppercase tracking-wider text-stone-500 mb-2">
            Choose a recommendation to review
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {GOVERNED_RECOMMENDATIONS.map((r) => {
              const selected = r.id === selectedId;
              return (
                <button
                  key={r.id}
                  data-testid={`gov-rec-${r.id}`}
                  onClick={() => setSelectedId(r.id)}
                  className={`text-left p-3 border rounded-sm bg-white transition-all ${
                    selected ? "border-stone-900 ring-1 ring-stone-900" : "border-stone-200 hover:border-stone-400"
                  }`}
                >
                  <div className="text-[10px] font-mono uppercase text-stone-400 tracking-wider">
                    {r.action_class}
                  </div>
                  <div className="text-sm font-medium text-stone-900 mt-1 leading-snug">
                    {r.title}
                  </div>
                  <div className="text-xs text-stone-500 mt-1 leading-relaxed">{r.summary}</div>
                </button>
              );
            })}
          </div>
        </section>

        {/* AI suggests · Custody · Authority */}
        <section className="col-span-12 grid grid-cols-1 lg:grid-cols-3 gap-3">
          {/* AI suggests */}
          <Card eyebrow="01 · AI suggests" title="Bounded recommendation">
            <p className="text-sm text-stone-800 leading-relaxed">{rec.summary}</p>
            <div className="mt-3 pt-2 border-t border-stone-100 text-[11px] text-stone-500">
              Action class
              <div className="font-mono text-stone-800 mt-0.5">{rec.action_class}</div>
            </div>
          </Card>

          {/* Custody */}
          <Card eyebrow="02 · Custody" title="Evidence stays at source" icon={<Lock size={14} />}>
            <div className="text-[11px] uppercase tracking-wider text-stone-500 mb-1">
              Shared references ({rec.custody.shared_evidence.length})
            </div>
            <ul className="space-y-1.5">
              {rec.custody.shared_evidence.map((e) => (
                <li key={e.ref} className="flex items-start gap-1.5 text-xs">
                  <Eye size={11} className="text-stone-400 mt-0.5 shrink-0" />
                  <div>
                    <span className="font-mono text-stone-800">{e.ref}</span>
                    <div className="text-stone-500 leading-snug">{e.note}</div>
                  </div>
                </li>
              ))}
            </ul>
            {rec.custody.withheld_evidence.length > 0 && (
              <>
                <div className="text-[11px] uppercase tracking-wider text-stone-500 mt-3 mb-1">
                  Withheld ({rec.custody.withheld_evidence.length})
                </div>
                <ul className="space-y-1.5">
                  {rec.custody.withheld_evidence.map((e, i) => (
                    <li key={i} className="flex items-start gap-1.5 text-xs">
                      <EyeSlash size={11} className="text-stone-400 mt-0.5 shrink-0" />
                      <span className="text-stone-700">{e.reason}</span>
                    </li>
                  ))}
                </ul>
              </>
            )}
            <div className="mt-3 pt-2 border-t border-stone-100 text-[11px] text-stone-500 leading-relaxed">
              {rec.boundary_note}
            </div>
          </Card>

          {/* Authority */}
          <Card eyebrow="03 · Authority" title="Authorised role decides" icon={<ShieldCheck size={14} />}>
            <div className="text-[11px] uppercase tracking-wider text-stone-500">Authorised role</div>
            <div className="text-sm font-medium text-stone-900 mt-0.5">{rec.actor.role}</div>
            <div className="font-mono text-[11px] text-stone-500">{rec.actor.ref}</div>
            <div className="text-[11px] uppercase tracking-wider text-stone-500 mt-3">Authority gate</div>
            <div className="font-mono text-xs text-stone-800 mt-0.5 break-all">{rec.authority_gate}</div>
            <div className="mt-3 pt-2 border-t border-stone-100 text-[11px] text-stone-500 leading-relaxed">
              The recommendation does not act on its own. The authorised role must decide. KATLAS
              records the governed action.
            </div>
          </Card>
        </section>

        {/* Decision buttons */}
        <section className="col-span-12 bg-white border border-stone-200 rounded-sm p-5">
          <div className="flex items-start justify-between gap-6 flex-wrap">
            <div>
              <div className="text-[11px] uppercase tracking-[0.18em] text-stone-500">
                04 · Governed decision
              </div>
              <div className="text-base font-medium text-stone-900 mt-0.5">
                Acting as <span className="text-stone-900">{rec.actor.role}</span>, approve or refuse the recommendation
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                data-testid="gov-refuse"
                onClick={() => decide("refused")}
                disabled={working}
                className="flex items-center gap-1.5 px-4 py-2 text-sm border border-stone-300 rounded-sm hover:bg-stone-50 disabled:opacity-50"
              >
                <XCircle size={14} /> Refuse
              </button>
              <button
                data-testid="gov-approve"
                onClick={() => decide("approved")}
                disabled={working}
                className="flex items-center gap-1.5 px-4 py-2 text-sm bg-stone-900 text-white rounded-sm hover:bg-stone-800 disabled:opacity-50"
              >
                <CheckCircle size={14} weight="fill" /> Approve <ArrowRight size={12} />
              </button>
            </div>
          </div>

          {working && (
            <div className="mt-4 text-sm text-stone-500 font-mono">Submitting governed action…</div>
          )}

          {result && <ResultBlock result={result} />}
        </section>
      </div>
    </div>
  );
};

const Card = ({ eyebrow, title, icon, children }) => (
  <div className="bg-white border border-stone-200 rounded-sm p-4 flex flex-col">
    <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.16em] text-stone-500">
      {icon}
      <span>{eyebrow}</span>
    </div>
    <div className="text-sm font-medium text-stone-900 mt-1 mb-2 leading-snug">{title}</div>
    <div className="flex-1 text-sm">{children}</div>
  </div>
);

const ResultBlock = ({ result }) => {
  if (!result.ok) {
    const data = result.error || {};
    const unconfigured = data.error === "katlas_unconfigured";
    return (
      <div
        data-testid="gov-result-error"
        className={`mt-4 border rounded-sm p-4 ${
          unconfigured ? "bg-amber-50 border-amber-200" : "bg-rose-50 border-rose-200"
        }`}
      >
        <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider font-medium">
          <Warning size={13} />
          {unconfigured ? "KATLAS unconfigured" : "KATLAS node error"}
        </div>
        <p className="text-sm mt-1.5 leading-relaxed">
          {data.message || "The governed action could not be signed by the KATLAS network."}
        </p>
        <div className="mt-2 text-[11px] font-mono text-stone-700">
          mode: {data.mode || "—"} · status: {data.status || "—"} · simulation_fallback_allowed: {String(data.simulation_fallback_allowed)}
        </div>
        <div className="mt-3 text-[11px] text-stone-500">
          No simulated receipt is issued. Set the production bearer token in the backend
          environment to enable live signing.
        </div>
      </div>
    );
  }
  return <ReceiptCard receipt={result.receipt} />;
};

const ReceiptCard = ({ receipt }) => (
  <div
    data-testid="gov-receipt"
    className="receipt-in mt-4 bg-[#FFFCF8] border border-dashed border-stone-300 rounded-sm p-5 paper-texture relative overflow-hidden"
  >
    <div className="relative z-10">
      <div className="flex items-start justify-between border-b border-stone-200 pb-2">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-stone-500">
            KATLAS · Decision record
          </div>
          <div className="font-serif italic text-xl text-stone-900 mt-0.5">Attributable receipt</div>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-wider text-stone-500">Hash</div>
          <div className="font-mono text-[11px] text-stone-900 break-all max-w-[260px]">
            {receipt.receipt_hash || "—"}
          </div>
        </div>
      </div>

      <dl className="grid grid-cols-2 gap-x-6 gap-y-1.5 mt-3 font-mono text-[11px] text-stone-700">
        <Row k="action" v={receipt.action} />
        <Row k="outcome" v={receipt.outcome} />
        <Row k="acting role" v={receipt.acting_role} />
        <Row k="authority gate" v={receipt.authority_gate} />
        <Row k="local time" v={receipt.issued_at_local} />
        <Row k="node time" v={receipt.node_timestamp || "—"} />
        <Row k="signer issuer" v={receipt.signer_issuer || "—"} />
        <Row k="signer key id" v={receipt.signer_key_id || "—"} />
        <Row k="signer fp" v={receipt.signer_fingerprint || "—"} />
        <Row k="verification" v={receipt.verification_status} />
        <Row
          k="evidence shared"
          v={
            (receipt.shared_evidence_refs || []).length
              ? receipt.shared_evidence_refs.join(", ")
              : "—"
          }
        />
        <Row k="withheld count" v={String(receipt.withheld_evidence_count ?? 0)} />
      </dl>

      <div className="mt-3 pt-2 border-t border-stone-200 grid grid-cols-1 md:grid-cols-3 gap-3 text-[11px]">
        <div>
          <div className="text-stone-400 uppercase tracking-wider">Custody</div>
          <div className="text-stone-800 leading-relaxed mt-0.5">{receipt.car?.custody}</div>
        </div>
        <div>
          <div className="text-stone-400 uppercase tracking-wider">Authority</div>
          <div className="text-stone-800 leading-relaxed mt-0.5">{receipt.car?.authority}</div>
        </div>
        <div>
          <div className="text-stone-400 uppercase tracking-wider">Receipts</div>
          <div className="text-stone-800 leading-relaxed mt-0.5">{receipt.car?.receipts}</div>
        </div>
      </div>

      <div className="mt-3 pt-2 border-t border-stone-200 flex items-start gap-2">
        <FileText size={12} className="text-stone-400 mt-0.5" />
        <p className="text-[11px] text-stone-600 leading-relaxed italic">
          {receipt.data_boundary_statement}
        </p>
      </div>

      {receipt._unsigned_reason && (
        <div className="mt-3 text-[10px] uppercase tracking-wider text-amber-700">
          Unsigned record · reason: {receipt._unsigned_reason}
        </div>
      )}
    </div>
  </div>
);

const Row = ({ k, v }) => (
  <div className="flex gap-3 min-w-0">
    <span className="text-stone-400 w-28 shrink-0 truncate">{k}</span>
    <span className="text-stone-800 flex-1 break-words">{v}</span>
  </div>
);
